import {useContext, useState} from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import {
  Button,
  Caption,
  DataTable,
  IconButton,
  Title,
} from 'react-native-paper';
import {useMutation, useQueryClient} from 'react-query';
import firestore from '@react-native-firebase/firestore';

import {useAppSettings} from '../components/AppSettings';
import {UserContext} from '../App';
import {filterMembers, Member, useMembers} from './../hooks/useDomainData';
import {
  injectPeople,
  Person,
  PersonHolder,
  useAuthPersonInfo,
  usePeople,
} from './../hooks/usePersonInfo';
import {PersonComponent} from './People';
import {MemberEditor} from '../components/FormikStoreEditors/MemberEditor';

export function MemberComponent(props: {
  member: Member;
  readOnly?: boolean;
}): JSX.Element {
  const people = usePeople();
  const person = useAuthPersonInfo();
  const appSettings = useAppSettings();
  const queryClient = useQueryClient();

  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [currentFirestoreObject, setCurrentFirestoreObject] = useState<
    Member | undefined
  >();

  const collectionName = 'members';
  const handleDeleteFirestoreObject = useMutation(
    (firestoreObject: Member) => {
      console.log('should delete: ' + firestoreObject.ref.path);
      return firestore()
        .collection(collectionName)
        .doc(firestoreObject.ref.id)
        .delete();
    },
    {
      onSettled: () => {
        console.log('deleting succeeded');
        return queryClient.invalidateQueries(collectionName);
      },
    },
  );

  const handleEditFirestoreObject = (firestoreObject?: Member) => {
    console.log('open for edit or new object: ' + firestoreObject?.ref.path);
    setCurrentFirestoreObject(firestoreObject);
    setEditDialogVisible(true);
  };

  return (
    <View key={props.member.memberId}>
      <View style={styles.horizontalSpaced}>
        <Caption
          key={
            props.member.memberId
          }>{`Member: ${props.member.memberName}`}</Caption>
        <View style={styles.horizontalEnd}>
          {!props.readOnly && (person?.isAdmin || person?.isGuestRelations) && (
            <>
              <Caption>Member Actions: </Caption>
              <IconButton
                icon="pencil"
                size={18}
                color={appSettings.currentTheme.colors.primary}
                onPress={() => handleEditFirestoreObject(props.member)}
              />
              {editDialogVisible && (
                <MemberEditor
                  dialogVisible={editDialogVisible}
                  setDialogVisible={(visible: boolean) => {
                    setEditDialogVisible(visible);
                    if (!visible) {
                      setCurrentFirestoreObject(undefined);
                    }
                  }}
                  member={currentFirestoreObject}
                  allPeople={Array.from(people ?? [])}
                />
              )}
            </>
          )}
          {!props.readOnly && person?.isAdmin && (
            <IconButton
              icon="delete"
              size={18}
              color={appSettings.currentTheme.colors.primary}
              onPress={() => handleDeleteFirestoreObject.mutate(props.member)}
            />
          )}
        </View>
      </View>
      <View style={styles.indentOnce}>
        <MemberPeople people={props.member.people} readOnly={props.readOnly} />
      </View>
    </View>
  );
}

function MemberPeople(props: {
  people?: Person[];
  readOnly?: boolean;
}): JSX.Element {
  return (
    <View>
      {!props.people && <Caption>loading people...</Caption>}
      {props.people &&
        props.people.map(person => (
          <PersonComponent
            key={person.authUid}
            person={person}
            readOnly={props.readOnly}
          />
        ))}
    </View>
  );
}

function Members() {
  const user = useContext(UserContext);
  let members = useMembers();
  const people = usePeople();
  const person = useAuthPersonInfo();
  const appSettings = useAppSettings();
  const queryClient = useQueryClient();

  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [currentFirestoreObject, setCurrentFirestoreObject] = useState<
    Member | undefined
  >();

  const collectionName = 'members';
  const handleDeleteFirestoreObject = useMutation(
    (firestoreObject: Member) => {
      console.log('should delete: ' + firestoreObject.ref.path);
      return firestore()
        .collection(collectionName)
        .doc(firestoreObject.ref.id)
        .delete();
    },
    {
      onSettled: () => {
        console.log('deleting succeeded');
        return queryClient.invalidateQueries(collectionName);
      },
    },
  );

  const handleEditFirestoreObject = (firestoreObject?: Member) => {
    setCurrentFirestoreObject(firestoreObject);
    setEditDialogVisible(true);
  };

  if (!user) {
    return null;
  }

  if (members !== undefined && people !== undefined) {
    members = Array.from(members);
    injectPeople(members as unknown as PersonHolder[], people);
  }

  if (!person?.isAdmin && !person?.isGuestRelations) {
    members = filterMembers(members);
  }

  // Sort things so that:
  const sortedMembers = Array.from(members ?? []);
  sortedMembers.sort((a, b) => {
    // - no associated people on top, sorted by name
    if (a.personIds.length === 0 && b.personIds.length === 0) {
      return a.memberName.localeCompare(b.memberName);
    }
    if (a.personIds.length === 0) {
      return -1;
    }
    if (b.personIds.length === 0) {
      return 1;
    }

    // now just sort by name
    return a.memberName.localeCompare(b.memberName);
  });

  // console.log('we got some members? ' + members?.length);
  return (
    <>
      <View style={styles.horizontalCentered}>
        <Title style={styles.title}>Members</Title>
        {(person?.isAdmin || person?.isGuestRelations) && (
          <Button mode="outlined" onPress={() => handleEditFirestoreObject()}>
            Create Member
          </Button>
        )}
      </View>
      <ScrollView
        persistentScrollbar={true}
        style={[styles.container, styles.content]}>
        <DataTable>
          {!members && (
            <DataTable.Row>
              <DataTable.Header>
                <DataTable.Title>loading...</DataTable.Title>
              </DataTable.Header>
            </DataTable.Row>
          )}
          {sortedMembers && (
            <DataTable.Header style={styles.shortHeader}>
              <DataTable.Title style={styles.center}>ID</DataTable.Title>
              <DataTable.Title style={styles.widerCell}>
                Member Name
              </DataTable.Title>
              <DataTable.Title style={styles.widerCell}>
                Linked People
              </DataTable.Title>
              <DataTable.Title style={styles.center}>Actions</DataTable.Title>
            </DataTable.Header>
          )}
          {sortedMembers && sortedMembers.length === 0 && (
            <DataTable.Row>
              <DataTable.Cell>No members found.</DataTable.Cell>
            </DataTable.Row>
          )}
          {sortedMembers &&
            sortedMembers.length > 0 &&
            sortedMembers.map(member => (
              <DataTable.Row key={member.memberId}>
                <DataTable.Cell>{member.memberId}</DataTable.Cell>
                <DataTable.Cell style={styles.widerCell}>
                  {member.memberName}
                </DataTable.Cell>
                <DataTable.Cell style={styles.widerCell}>
                  {member.people &&
                    member.people
                      .map(
                        memberPerson =>
                          `${memberPerson.firstName} ${memberPerson.lastName}`,
                      )
                      .join(', ')}
                </DataTable.Cell>
                <DataTable.Cell>
                  {(person?.isAdmin || person?.isGuestRelations) && (
                    <>
                      <IconButton
                        icon="pencil"
                        size={18}
                        color={appSettings.currentTheme.colors.primary}
                        onPress={() => handleEditFirestoreObject(member)}
                      />
                    </>
                  )}
                  {person?.isAdmin && (
                    <IconButton
                      icon="delete"
                      size={18}
                      color={appSettings.currentTheme.colors.primary}
                      onPress={() => handleDeleteFirestoreObject.mutate(member)}
                    />
                  )}
                </DataTable.Cell>
              </DataTable.Row>
            ))}
        </DataTable>
      </ScrollView>
      <MemberEditor
        dialogVisible={editDialogVisible}
        setDialogVisible={(visible: boolean) => {
          setEditDialogVisible(visible);
          if (!visible) {
            setCurrentFirestoreObject(undefined);
          }
        }}
        member={currentFirestoreObject}
        allPeople={Array.from(people ?? [])}
      />
    </>
  );
}

const styles = StyleSheet.create({
  indentOnce: {paddingLeft: 20, flexDirection: 'row'},
  widerCell: {
    minWidth: 150,
    width: 130,
    justifyContent: 'center',
    alignItems: 'center',
  },
  borderCell: {
    borderStartWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    flex: 1,
    position: 'relative',
  },
  content: {
    paddingHorizontal: 20,
  },
  center: {
    alignItems: 'center',
  },
  shortHeader: {
    height: 25,
  },
  title: {
    padding: 10,
    paddingHorizontal: 20,
  },
  horizontalSpaced: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  horizontalEnd: {
    flex: 1,
    flexDirection: 'row',
    paddingHorizontal: 10,
    justifyContent: 'flex-end',
    alignSelf: 'flex-end',
    alignItems: 'center',
  },
  horizontalCentered: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignContent: 'center',
    paddingHorizontal: 10,
  },
});

export default Members;
